import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    LuArrowRight, LuChartNoAxesColumn, LuCheck, LuChevronLeft, LuChevronRight, LuClipboardList,
    LuContact, LuCopy, LuExternalLink, LuIdCard, LuLoaderCircle, LuMegaphone, LuPhone, LuSend, LuTrash2,
    LuTriangleAlert, LuX, LuHistory, LuUpload, LuDownload,
} from 'react-icons/lu';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import PageHeader, { type PageStep } from '../components/PageHeader';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import WhatsAppConnectionGuard from '../components/WhatsAppConnectionGuard';
import Dialog from '../components/Dialog';
import Select from '../components/Select';
import TemplateBubble, { lerComponentes, type TemplateVisual } from '../components/TemplateBubble';
import { PerPage } from '../components/ListKit';
import { PERMISSOES, temPermissao } from '../config/permissions';
import {
    CampaignService, rotuloDisparo,
    type Campaign, type CampanhaCategoria, type CampanhaProgresso, type CampanhaResumo,
    type ComponenteTemplate, type Disparo, type DispararResult,
} from '../services/CampaignService';
import { ContactService, type Contact } from '../services/ContactService';
import { MetaTemplateService, type MetaTemplate } from '../services/MetaTemplateService';
import './Disparador.css';

/**
 * A campanha: os quatro passos de sempre — Campanha · Público · Conteúdo ·
 * Revisão — agora numa página, e não numa modal de 960px.
 *
 * Desenho fechado em docs/designsystem/propostas/campanhas.html (18/09/2026).
 * Ele recusou as três direções que trocavam o wizard por outra coisa: *"queria
 * tentar manter um padrão de steps para a campanha e disparo de msg"*. Então a
 * mecânica é a mesma; o que mudou é a moldura, a barra de passos no cabeçalho
 * e o telefone docado, vivo desde o passo 3.
 *
 * Depois do disparo a mesma rota vira o resultado, em leitura: funil, disparos
 * e a ficha. Não há volta — o back só aceita disparo em Rascunho.
 */

/* ── Tipos locais ──────────────────────────────────────────────── */

type Passo = 1 | 2 | 3 | 4;
type Porta = 'base' | 'telefone' | 'colar';
type FonteVar = 'nome' | 'telefone' | 'fixo';

interface Mapeamento {
    fonte: FonteVar;
    valorFixo: string;
}

/**
 * Um destinatário como a TELA o conhece. O `nome` é `null` quando não se sabe
 * — e continua `null`: inventar `Contato <telefone>` (o que a tela antiga
 * fazia) não ficava só na lista, ia parar DENTRO da mensagem sempre que uma
 * variável estivesse mapeada para o nome do contato.
 */
interface Alvo {
    nome: string | null;
    telefone: string;
    origem: 'base' | 'telefone' | 'colado';
    /** Presente quando já está salvo na API. */
    destinatarioId?: number;
}

const FONTE_OPCOES = [
    { value: 'nome', label: 'Nome do contato' },
    { value: 'telefone', label: 'Telefone do contato' },
    { value: 'fixo', label: 'Valor fixo (igual para todos)' },
];

const ORIGEM_ROTULO: Record<Alvo['origem'], string> = {
    base: 'da base', telefone: 'por telefone', colado: 'colado',
};

const PASSOS: Array<{ id: string; label: string }> = [
    { id: '1', label: 'Campanha' },
    { id: '2', label: 'Público' },
    { id: '3', label: 'Conteúdo' },
    { id: '4', label: 'Revisão' },
];

/** Referência, não medição: a Meta cobra por categoria e país. */
const CUSTO_REFERENCIA = 0.31;

const sonumeros = (v: string) => v.replace(/\D/g, '');

const telefoneBonito = (v: string) => {
    const n = sonumeros(v);
    if (n.length < 12) return v;
    return `+${n.slice(0, 2)} ${n.slice(2, 4)} ${n.slice(4, -4)}-${n.slice(-4)}`;
};

const dataHora = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const dataLonga = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Telefone como a Meta quer: só dígitos, com DDI. Número brasileiro sem DDI
 * (10 ou 11 dígitos, já com DDD) ganha o 55; zero de discagem na frente sai.
 * Retorna `null` quando não dá para confiar no número.
 */
const normalizarTelefone = (v: string): string | null => {
    const n = sonumeros(v).replace(/^0+/, '');
    if (n.length === 10 || n.length === 11) return `55${n}`;
    if (n.length >= 12 && n.length <= 15) return n;
    return null;
};

/** Célula que é só telefone: dígitos e a pontuação de sempre — `+55 (41) 99999-9999`. */
const ehCelulaTelefone = (v: string) => /^[\d\s()+\-.]+$/.test(v.trim()) && sonumeros(v).length >= 8;

/** Trecho de telefone no meio de um texto, para `Maria 41 99999-9999`. */
const TELEFONE_NO_TEXTO = /\+?\d[\d\s().-]{6,}\d/;

interface LinhaIgnorada { linha: string; motivo: string; }

interface Colagem {
    validos: Alvo[];
    ignoradas: LinhaIgnorada[];
    repetidos: number;
}

/**
 * Lê o que a pessoa colou do jeito que vier: `Nome, telefone`, `telefone; nome`,
 * colunas copiadas do Excel/Sheets (tab), `Maria 41 99999-9999` ou só o número.
 * O telefone é a célula que parece telefone, em qualquer posição; o nome, a
 * primeira com letra que não é e-mail. Linha sem dígito nenhum (o cabeçalho da
 * planilha) passa calada; linha com número ruim volta em `ignoradas` com o motivo.
 * Sem nome é `null`, não um nome falso.
 */
const lerColados = (cru: string): Colagem => {
    const vistos = new Set<string>();
    const validos: Alvo[] = [];
    const ignoradas: LinhaIgnorada[] = [];
    let repetidos = 0;

    for (const bruta of cru.split(/\r?\n/)) {
        const linha = bruta.trim();
        if (!linha || !/\d/.test(linha)) continue;

        const partes = linha.split(/[,;\t|]/).map(p => p.trim().replace(/^"|"$/g, '')).filter(Boolean);
        let celulaTelefone = partes.find(ehCelulaTelefone);
        let resto = partes.filter(p => p !== celulaTelefone);

        if (!celulaTelefone) {
            const achado = linha.match(TELEFONE_NO_TEXTO);
            if (achado) {
                celulaTelefone = achado[0];
                resto = [linha.replace(achado[0], ' ').replace(/[,;\t|]+/g, ' ')];
            }
        }

        if (!celulaTelefone) {
            ignoradas.push({ linha, motivo: 'nenhum telefone encontrado' });
            continue;
        }
        const telefone = normalizarTelefone(celulaTelefone);
        if (!telefone) {
            ignoradas.push({ linha, motivo: 'telefone incompleto — falta o DDD?' });
            continue;
        }
        if (vistos.has(telefone)) { repetidos += 1; continue; }
        vistos.add(telefone);

        const nome = resto.map(p => p.replace(/\s+/g, ' ').trim())
            .find(p => /\p{L}/u.test(p) && !p.includes('@'));
        validos.push({ nome: nome || null, telefone, origem: 'colado' });
    }
    return { validos, ignoradas, repetidos };
};

/** Modelo que a pessoa baixa, preenche no Excel/Sheets e cola (ou importa) de volta. */
const MODELO_CSV = 'nome;telefone\nMaria Souza;(41) 99999-9999\nJoão Lima;5541988888888\n';

const BOM = String.fromCharCode(0xfeff);

const baixarModelo = () => {
    /* BOM para o Excel abrir os acentos certos. */
    const blob = new Blob([BOM + MODELO_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo-publico.csv';
    a.click();
    URL.revokeObjectURL(url);
};

/* ── Tela ──────────────────────────────────────────────────────── */

const CampanhaPage = () => {
    const navegar = useNavigate();
    const { id } = useParams<{ id: string }>();
    const campanhaId = id && id !== 'nova' ? Number(id) : null;
    /* Sem `marketing_disparador.gerenciar` a campanha só é vista: somem salvar,
       disparar, duplicar e excluir (a API recusa todos eles sem a chave). Os
       destinatários mexidos na tela ficam só na memória — nada é gravado. */
    const podeGerenciar = temPermissao(PERMISSOES.marketingDisparadorGerenciar);

    const [carregando, setCarregando] = useState(true);
    const [campanha, setCampanha] = useState<Campaign | null>(null);
    const [resumo, setResumo] = useState<CampanhaResumo | null>(null);
    const [passo, setPasso] = useState<Passo>(1);

    /* passo 1 */
    const [nome, setNome] = useState('');
    const [paraIA, setParaIA] = useState(true);

    /* passo 2 */
    const [porta, setPorta] = useState<Porta>('base');
    const [contatos, setContatos] = useState<Contact[]>([]);
    const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
    const [buscaBase, setBuscaBase] = useState('');
    const [organizacao, setOrganizacao] = useState('');
    const [manualNome, setManualNome] = useState('');
    const [manualTelefone, setManualTelefone] = useState('');
    const [manualErro, setManualErro] = useState('');
    const [colados, setColados] = useState('');
    const [filtroAdd, setFiltroAdd] = useState('');
    const [alvos, setAlvos] = useState<Alvo[]>([]);
    const [removidos, setRemovidos] = useState<number[]>([]);

    /* passo 3 */
    const [templates, setTemplates] = useState<MetaTemplate[]>([]);
    const [templateNome, setTemplateNome] = useState('');
    const [mapeamentos, setMapeamentos] = useState<Mapeamento[]>([]);
    const [cupom, setCupom] = useState('');
    const [urlVar, setUrlVar] = useState('');
    const [local, setLocal] = useState({ nome: '', endereco: '', lat: '', lng: '' });
    const [buscandoCoord, setBuscandoCoord] = useState(false);

    /**
     * A assinatura do mapeamento que gerou os destinatários JÁ SALVOS.
     * As variáveis são resolvidas no momento de salvar e gravadas por
     * destinatário; sem isto, voltar ao passo 3 e trocar o mapeamento não
     * alcançaria quem já estava salvo, e o disparo sairia com o valor antigo.
     */
    const [assinaturaSalva, setAssinaturaSalva] = useState<string | null>(null);

    /* resultado */
    const [disparos, setDisparos] = useState<Disparo[]>([]);
    const [totalDisparos, setTotalDisparos] = useState(0);
    const [paginaDisparos, setPaginaDisparos] = useState(1);
    const [porPagina, setPorPagina] = useState(20);
    const [secao, setSecao] = useState<'resultado' | 'disparos' | 'ficha' | 'historico'>('resultado');

    const [salvando, setSalvando] = useState(false);
    const [disparando, setDisparando] = useState(false);
    const [confirmarDisparo, setConfirmarDisparo] = useState(false);
    const [confirmarExclusao, setConfirmarExclusao] = useState(false);
    /** A resposta do disparo: abre o "Disparo iniciado" com Acompanhar ou Sair. */
    const [disparoIniciado, setDisparoIniciado] = useState<DispararResult | null>(null);
    /** Só enquanto a campanha está em Enviando; vem do polling de `progresso`. */
    const [progresso, setProgresso] = useState<CampanhaProgresso | null>(null);
    /** Sobe para reler a campanha sem recarregar a página (fim do envio, "Acompanhar envio"). */
    const [versao, setVersao] = useState(0);

    const finalizada = campanha !== null && campanha.campanhaStatus !== 'Rascunho';
    const enviando = campanha?.campanhaStatus === 'Enviando';

    /* ── carregar ───────────────────────────────────────────── */

    useEffect(() => {
        let vivo = true;
        const carregar = async () => {
            setCarregando(true);
            try {
                const [tpl, cts] = await Promise.all([
                    MetaTemplateService.getAll(),
                    ContactService.getAll(),
                ]);
                if (!vivo) return;
                if (tpl.sucesso) setTemplates((tpl.dados ?? []).filter(t => t.status === 'APPROVED'));
                if (cts.sucesso) setContatos(cts.dados ?? []);

                if (campanhaId === null) { setCarregando(false); return; }

                const res = await CampaignService.buscarPorId(campanhaId);
                if (!vivo) return;
                if (!res.sucesso || !res.dados) {
                    aviso.erro('Campanha não encontrada.');
                    navegar('/campanhas');
                    return;
                }
                const c = res.dados;
                setCampanha(c);
                setNome(c.campanhaNome);
                // O nome REAL do campo. Ler `campanhaRedirecionarRespostaParaIA`
                // dava `undefined`, que virava `false` e desligava o Agente de IA
                // a cada edição salva.
                setParaIA(c.redirecionarRespostaParaIA);
                setTemplateNome(c.campanhaTemplateNome);

                if (c.campanhaStatus === 'Rascunho') {
                    const dest = await CampaignService.getDestinatarios(campanhaId);
                    if (!vivo) return;
                    if (dest.sucesso) {
                        setAlvos((dest.dados ?? []).map(d => ({
                            // A API não guarda nome — só número e variáveis. Então
                            // aqui ele é desconhecido de verdade, e "Sem nome" é a
                            // leitura honesta.
                            nome: null,
                            telefone: d.numero ?? d.bsuid ?? '',
                            origem: 'base' as const,
                            destinatarioId: d.destinatarioId,
                        })));
                    }
                } else {
                    const [resu, conv, prog] = await Promise.all([
                        CampaignService.buscarResumo(),
                        CampaignService.getConversas(campanhaId, 1, 20),
                        c.campanhaStatus === 'Enviando' ? CampaignService.buscarProgresso(campanhaId) : Promise.resolve(null),
                    ]);
                    if (!vivo) return;
                    setProgresso(prog?.sucesso ? prog.dados : null);
                    if (resu.sucesso) {
                        setResumo((resu.dados ?? []).find(r => r.campanhaId === campanhaId) ?? null);
                    }
                    if (conv.sucesso && conv.dados) {
                        setDisparos(conv.dados.itens);
                        setTotalDisparos(conv.dados.total);
                    }
                }
            } catch {
                if (vivo) aviso.erro(SEM_SERVIDOR);
            } finally {
                if (vivo) setCarregando(false);
            }
        };
        carregar();
        return () => { vivo = false; };
    }, [campanhaId, navegar, versao]);

    /*
     * O envio corre no servidor; aqui só se pergunta quanto já saiu, a cada 2 s,
     * enquanto a campanha estiver em Enviando e a aba estiver à vista. Erro de rede
     * não para nada: o próximo tique tenta de novo. Quando o servidor diz que
     * acabou, a campanha é relida e a tela vira o resultado.
     */
    useEffect(() => {
        if (!enviando || campanhaId === null || carregando) return;
        let vivo = true;
        /* Uma pergunta por vez: rede lenta não empilha pedidos nem avisa o fim duas vezes. */
        let emCurso = false;
        const perguntar = async () => {
            if (document.hidden || emCurso || !vivo) return;
            emCurso = true;
            try {
                const res = await CampaignService.buscarProgresso(campanhaId);
                if (!vivo || !res.sucesso || !res.dados) return;
                setProgresso(res.dados);
                if (secao === 'disparos') {
                    const conv = await CampaignService.getConversas(campanhaId, paginaDisparos, porPagina);
                    if (vivo && conv.sucesso && conv.dados) {
                        setDisparos(conv.dados.itens);
                        setTotalDisparos(conv.dados.total);
                    }
                }
                if (res.dados.concluido && vivo) {
                    vivo = false;
                    const { enviados, falhas } = res.dados;
                    if (enviados === 0 && falhas > 0) {
                        aviso.erro('Nenhuma mensagem saiu. Veja o motivo de cada falha na aba Disparos.');
                    } else {
                        aviso.ok(`Disparo concluído: ${enviados} ${enviados === 1 ? 'enviada' : 'enviadas'}, ${falhas} ${falhas === 1 ? 'falha' : 'falhas'}.`);
                    }
                    setVersao(v => v + 1);
                }
            } catch { /* tenta no próximo tique */ } finally {
                emCurso = false;
            }
        };
        const relogio = window.setInterval(perguntar, 2000);
        /* Voltar para a aba não espera o próximo tique. */
        const aoVoltar = () => { if (!document.hidden) perguntar(); };
        document.addEventListener('visibilitychange', aoVoltar);
        return () => {
            vivo = false;
            window.clearInterval(relogio);
            document.removeEventListener('visibilitychange', aoVoltar);
        };
    }, [enviando, campanhaId, carregando, secao, paginaDisparos, porPagina]);

    /* Paginação da tabela de disparos, já em modo leitura. */
    useEffect(() => {
        if (!finalizada || campanhaId === null || carregando) return;
        let vivo = true;
        CampaignService.getConversas(campanhaId, paginaDisparos, porPagina).then(res => {
            if (!vivo || !res.sucesso || !res.dados) return;
            setDisparos(res.dados.itens);
            setTotalDisparos(res.dados.total);
        }).catch(() => { /* a tabela fica com o que já tinha */ });
        return () => { vivo = false; };
    }, [finalizada, campanhaId, paginaDisparos, porPagina, carregando]);

    /* ── o template escolhido e o que ele pede ──────────────── */

    const template = useMemo(
        () => templates.find(t => t.nome === templateNome) ?? null,
        [templates, templateNome]);

    const visualBase = useMemo(
        () => lerComponentes(template?.componentesJson ?? null, template?.caminhoMidiaHeader),
        [template]);

    /** Quantas variáveis `{{n}}` o corpo tem, pela maior delas. */
    const totalVars = useMemo(() => {
        const achadas = [...visualBase.textoBody.matchAll(/\{\{(\d+)\}\}/g)].map(m => Number(m[1]));
        return achadas.length ? Math.max(...achadas) : 0;
    }, [visualBase.textoBody]);

    const pedeLocalizacao = visualBase.formatoHeader === 'LOCATION';
    const indiceCupom = visualBase.botoes.findIndex(b => b.tipo === 'COPY_CODE');
    const indiceUrlVar = useMemo(() => {
        try {
            const comps = JSON.parse(template?.componentesJson ?? '[]') as Array<Record<string, unknown>>;
            const btns = (comps.find(c => c.type === 'BUTTONS')?.buttons as Array<Record<string, unknown>>) ?? [];
            return btns.findIndex(b => b.type === 'URL' && String(b.url ?? '').includes('{{1}}'));
        } catch { return -1; }
    }, [template]);

    /* Trocar de template refaz o mapeamento: o número de variáveis muda. */
    const escolherTemplate = (valor: string) => {
        setTemplateNome(valor);
        const novo = templates.find(t => t.nome === valor);
        const qtd = novo
            ? (() => {
                const achadas = [...lerComponentes(novo.componentesJson).textoBody.matchAll(/\{\{(\d+)\}\}/g)]
                    .map(m => Number(m[1]));
                return achadas.length ? Math.max(...achadas) : 0;
            })()
            : 0;
        setMapeamentos(Array.from({ length: qtd }, () => ({ fonte: 'nome' as FonteVar, valorFixo: '' })));
        setCupom('');
        setUrlVar('');
    };

    /* Ao abrir um rascunho, o mapeamento nasce do template que ele já tem. */
    useEffect(() => {
        if (!template || mapeamentos.length === totalVars) return;
        setMapeamentos(Array.from({ length: totalVars }, (_, i) =>
            mapeamentos[i] ?? { fonte: 'nome' as FonteVar, valorFixo: '' }));
    }, [template, totalVars, mapeamentos]);

    const resolverVar = useCallback((m: Mapeamento, alvo: Alvo | undefined): string => {
        if (m.fonte === 'nome') return alvo?.nome ?? '';
        if (m.fonte === 'telefone') return alvo?.telefone ?? '';
        return m.valorFixo;
    }, []);

    /** A prévia mostra o primeiro destinatário, que é quem vai receber primeiro. */
    const visualPrevia: TemplateVisual = useMemo(() => ({
        ...visualBase,
        exemplos: mapeamentos.map(m => resolverVar(m, alvos[0])),
    }), [visualBase, mapeamentos, alvos, resolverVar]);

    /* ── público ────────────────────────────────────────────── */

    const organizacoes = useMemo(() => {
        const nomes = new Set<string>();
        for (const c of contatos) if (c.organizacaoNome) nomes.add(c.organizacaoNome);
        return [...nomes].sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [contatos]);

    const jaAdicionados = useMemo(() => new Set(alvos.map(a => a.telefone)), [alvos]);

    const contatosFiltrados = useMemo(() => {
        const termo = buscaBase.trim().toLowerCase();
        return contatos.filter(c => {
            const casa = !termo
                || (c.nome ?? '').toLowerCase().includes(termo)
                || (c.telefone ?? '').includes(termo);
            const casaOrg = !organizacao || c.organizacaoNome === organizacao;
            return casa && casaOrg;
        });
    }, [contatos, buscaBase, organizacao]);

    const alvosFiltrados = useMemo(() => {
        const termo = filtroAdd.trim().toLowerCase();
        if (!termo) return alvos;
        return alvos.filter(a =>
            (a.nome ?? '').toLowerCase().includes(termo) || a.telefone.includes(termo));
    }, [alvos, filtroAdd]);

    const alternarContato = (contatoId: number) => {
        setSelecionados(antes => {
            const novo = new Set(antes);
            if (novo.has(contatoId)) novo.delete(contatoId); else novo.add(contatoId);
            return novo;
        });
    };

    const adicionarSelecionados = () => {
        const novos: Alvo[] = contatos
            .filter(c => selecionados.has(c.contatoId))
            .map(c => ({ nome: c.nome || null, telefone: sonumeros(c.telefone ?? ''), origem: 'base' as const }))
            .filter(a => a.telefone.length >= 8 && !jaAdicionados.has(a.telefone));
        if (novos.length === 0) {
            aviso.atencao('Os contatos escolhidos já estão na lista.');
            return;
        }
        setAlvos(antes => [...antes, ...novos]);
        setSelecionados(new Set());
    };

    const adicionarManual = () => {
        const telefone = normalizarTelefone(manualTelefone);
        if (!telefone) { setManualErro('Informe o telefone com DDD.'); return; }
        if (jaAdicionados.has(telefone)) { setManualErro('Este número já está na lista.'); return; }
        setManualErro('');
        setAlvos(antes => [...antes, {
            nome: manualNome.trim() || null, telefone, origem: 'telefone' as const,
        }]);
        setManualNome('');
        setManualTelefone('');
    };

    const colagem = useMemo(() => lerColados(colados), [colados]);
    const detectados = colagem.validos;

    /* .csv/.txt do computador cai no mesmo campo: a pessoa vê o que entrou antes de adicionar. */
    const importarArquivo = (e: ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        e.target.value = '';
        if (!arquivo) return;
        if (!/\.(csv|txt)$/i.test(arquivo.name)) {
            aviso.atencao('Envie um arquivo .csv ou .txt. No Excel: Salvar como → CSV.');
            return;
        }
        arquivo.text().then(texto => setColados(texto.startsWith(BOM) ? texto.slice(1) : texto));
    };

    const adicionarColados = () => {
        const novos = detectados.filter(a => !jaAdicionados.has(a.telefone));
        if (novos.length === 0) {
            aviso.atencao('Nenhum número novo na lista colada.');
            return;
        }
        setAlvos(antes => [...antes, ...novos]);
        /* As linhas que não entraram ficam no campo para a pessoa corrigir. */
        setColados(colagem.ignoradas.map(i => i.linha).join('\n'));
    };

    const removerAlvo = (alvo: Alvo) => {
        if (alvo.destinatarioId) setRemovidos(antes => [...antes, alvo.destinatarioId!]);
        setAlvos(antes => antes.filter(a => a.telefone !== alvo.telefone));
    };

    const limparTodos = () => {
        setRemovidos(antes => [...antes, ...alvos.filter(a => a.destinatarioId).map(a => a.destinatarioId!)]);
        setAlvos([]);
    };

    /* ── validação por passo ────────────────────────────────── */

    const faltaNoPasso = (p: Passo): string | null => {
        if (p === 1) return nome.trim() ? null : 'Dê um nome à campanha.';
        if (p === 2) return alvos.length > 0 ? null : 'Escolha ao menos um contato.';
        if (p === 3) {
            if (!templateNome) return 'Escolha o template.';
            const fixoVazio = mapeamentos.some(m => m.fonte === 'fixo' && !m.valorFixo.trim());
            if (fixoVazio) return 'Preencha o valor fixo de todas as variáveis.';
            if (indiceCupom !== -1 && !cupom.trim()) return 'Informe o código do cupom.';
            if (indiceUrlVar !== -1 && !urlVar.trim()) return 'Informe a variável do link.';
            if (pedeLocalizacao && (!local.nome.trim() || !local.endereco.trim() || !local.lat.trim() || !local.lng.trim()))
                return 'Preencha nome, endereço e coordenadas.';
            return null;
        }
        return null;
    };

    const cumpridos = useMemo(() => ({
        1: faltaNoPasso(1) === null,
        2: faltaNoPasso(2) === null,
        3: faltaNoPasso(3) === null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [nome, alvos, templateNome, mapeamentos, cupom, urlVar, local, indiceCupom, indiceUrlVar, pedeLocalizacao]);

    /** Reabrir um rascunho cai no primeiro passo incompleto (decisão de 18/09). */
    useEffect(() => {
        if (carregando || campanha === null || finalizada) return;
        setPasso(p => (p !== 1 ? p : !cumpridos[1] ? 1 : !cumpridos[2] ? 2 : !cumpridos[3] ? 3 : 4));
        // Só na chegada: depois disso quem manda é a barra de passos.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carregando, campanha, finalizada]);

    const passosDoHeader: PageStep[] = PASSOS.map((p, i) => ({
        ...p,
        done: i < 3 ? cumpridos[(i + 1) as 1 | 2 | 3] : false,
        meta: p.id === '2' && alvos.length > 0 ? `· ${alvos.length}` : undefined,
    }));

    /* ── salvar e disparar ──────────────────────────────────── */

    const assinaturaAtual = JSON.stringify(mapeamentos);

    const categoriaDoTemplate = (template?.categoria ?? campanha?.campanhaCategoria ?? 'MARKETING') as CampanhaCategoria;
    const idiomaDoTemplate = template?.idioma ?? campanha?.campanhaTemplateIdioma ?? 'pt_BR';

    /** Grava a campanha e concilia os destinatários. Devolve o id, ou null. */
    const gravar = useCallback(async (): Promise<number | null> => {
        const base = {
            nome: nome.trim(),
            templateNome,
            templateIdioma: idiomaDoTemplate,
            categoria: categoriaDoTemplate,
            redirecionarRespostaParaIA: paraIA,
        };

        let alvoId = campanha?.campanhaId ?? null;

        if (alvoId === null) {
            const res = await CampaignService.create(base);
            if (!res.sucesso || !res.dados) {
                aviso.erro(res.mensagem || 'Não foi possível salvar a campanha.');
                return null;
            }
            alvoId = res.dados.campanhaId;
            setCampanha(res.dados);
        } else {
            const res = await CampaignService.update({ ...base, campanhaId: alvoId, status: 'Rascunho' });
            if (!res.sucesso) {
                aviso.erro(res.mensagem || 'Não foi possível atualizar a campanha.');
                return null;
            }
        }

        // Mapeamento mudou desde a última gravação: quem já está salvo carrega
        // o valor antigo, e não existe endpoint para atualizar variável. Então
        // apaga e regrava todo mundo.
        const mudouMapeamento = assinaturaSalva !== null && assinaturaSalva !== assinaturaAtual;
        const paraRemover = mudouMapeamento
            ? [...removidos, ...alvos.filter(a => a.destinatarioId).map(a => a.destinatarioId!)]
            : removidos;
        const paraAdicionar = mudouMapeamento ? alvos : alvos.filter(a => !a.destinatarioId);

        for (const destinatarioId of paraRemover) {
            await CampaignService.removeDestinatario(alvoId, destinatarioId);
        }
        if (paraAdicionar.length > 0) {
            const res = await CampaignService.addDestinatarios(alvoId, paraAdicionar.map(a => ({
                numero: a.telefone,
                variaveis: mapeamentos.map(m => resolverVar(m, a)),
            })));
            if (!res.sucesso) {
                aviso.erro(res.mensagem || 'Não foi possível salvar os contatos.');
                return null;
            }
        }
        setRemovidos([]);
        setAssinaturaSalva(assinaturaAtual);

        // Relê para saber os ids novos, preservando os nomes que só a tela tem.
        const dest = await CampaignService.getDestinatarios(alvoId);
        if (dest.sucesso) {
            const porNumero = new Map(alvos.map(a => [a.telefone, a]));
            setAlvos((dest.dados ?? []).map(d => {
                const numero = d.numero ?? d.bsuid ?? '';
                const local = porNumero.get(numero);
                return {
                    nome: local?.nome ?? null,
                    telefone: numero,
                    origem: local?.origem ?? 'base',
                    destinatarioId: d.destinatarioId,
                };
            }));
        }
        return alvoId;
    }, [nome, templateNome, idiomaDoTemplate, categoriaDoTemplate, paraIA, campanha,
        assinaturaSalva, assinaturaAtual, removidos, alvos, mapeamentos, resolverVar]);

    const salvarRascunho = async () => {
        const falta = faltaNoPasso(1);
        if (falta) { aviso.atencao(falta); return; }
        setSalvando(true);
        try {
            const alvoId = await gravar();
            if (alvoId !== null) {
                aviso.ok('Rascunho salvo.');
                if (campanhaId === null) navegar(`/campanhas/${alvoId}`, { replace: true });
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
        }
    };

    const montarComponentes = (): ComponenteTemplate[] => {
        const saida: ComponenteTemplate[] = [];
        if (pedeLocalizacao) {
            saida.push({
                tipo: 'HEADER',
                parametros: [{
                    tipo: 'location',
                    localizacao: {
                        latitude: local.lat.trim(),
                        longitude: local.lng.trim(),
                        nome: local.nome.trim(),
                        endereco: local.endereco.trim(),
                    },
                }],
            });
        }
        if (indiceCupom !== -1) {
            saida.push({
                tipo: 'BUTTON', subTipo: 'COPY_CODE', indicesBotao: indiceCupom,
                parametros: [{ tipo: 'coupon_code', codigoCupom: cupom.trim() }],
            });
        }
        if (indiceUrlVar !== -1) {
            saida.push({
                tipo: 'BUTTON', subTipo: 'URL', indicesBotao: indiceUrlVar,
                parametros: [{ tipo: 'text', texto: urlVar.trim() }],
            });
        }
        return saida;
    };

    const dispararAgora = async () => {
        setConfirmarDisparo(false);
        setDisparando(true);
        try {
            const alvoId = await gravar();
            if (alvoId === null) return;
            const res = await CampaignService.disparar(alvoId, { componentes: montarComponentes() });
            if (res.sucesso && res.dados) {
                // Já está na fila do servidor: daqui em diante a tela só acompanha.
                setDisparoIniciado(res.dados);
            } else {
                aviso.erro(res.mensagem || 'Não foi possível disparar a campanha.');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setDisparando(false);
        }
    };

    /** "Acompanhar envio": a mesma rota, relida, já mostra o progresso. */
    const acompanharEnvio = () => {
        const alvoId = disparoIniciado?.campanhaId ?? null;
        setDisparoIniciado(null);
        setSecao('resultado');
        if (alvoId === null) return;
        if (alvoId === campanhaId) setVersao(v => v + 1);
        else navegar(`/campanhas/${alvoId}`, { replace: true });
    };

    const excluir = async () => {
        if (campanha === null) { navegar('/campanhas'); return; }
        setConfirmarExclusao(false);
        try {
            const res = await CampaignService.remove(campanha.campanhaId);
            if (res.sucesso) { aviso.ok('Campanha removida.'); navegar('/campanhas'); }
            else aviso.erro(res.mensagem || 'Não foi possível remover a campanha.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        }
    };

    const duplicar = () => {
        navegar('/campanhas/nova', {
            state: { template: templateNome, alvos: alvos.map(a => ({ ...a, destinatarioId: undefined })) },
        });
        aviso.info('Campanha nova com o mesmo template e os mesmos destinatários.');
    };

    const buscarCoordenadas = async () => {
        if (!local.endereco.trim()) return;
        setBuscandoCoord(true);
        try {
            const url = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/'
                + `findAddressCandidates?f=json&singleLine=${encodeURIComponent(local.endereco)}&maxLocations=1`;
            const r = await fetch(url);
            const dados = r.ok ? await r.json() : null;
            const achado = dados?.candidates?.[0]?.location;
            if (achado) {
                setLocal(l => ({ ...l, lat: String(achado.y), lng: String(achado.x) }));
                aviso.ok('Coordenadas preenchidas.');
            } else {
                aviso.atencao('Não achei esse endereço. Preencha as coordenadas à mão.');
            }
        } catch {
            aviso.erro('Não foi possível consultar o endereço.');
        } finally {
            setBuscandoCoord(false);
        }
    };

    /* ── checagens de "Antes de disparar" ───────────────────── */

    const semNome = alvos.filter(a => !a.nome).length;
    const usaNome = mapeamentos.some(m => m.fonte === 'nome');
    const semDDI = alvos.filter(a => a.telefone.length < 12).length;

    const checagens = [
        { ok: !!template, texto: 'Template aprovado na Meta' },
        {
            ok: semDDI === 0,
            texto: semDDI === 0
                ? `${alvos.length} ${alvos.length === 1 ? 'número' : 'números'} com DDI e DDD`
                : `${semDDI} ${semDDI === 1 ? 'número parece' : 'números parecem'} estar sem DDI ou DDD`,
        },
        { ok: faltaNoPasso(3) === null, texto: 'Variáveis e campos do template preenchidos' },
        ...(usaNome && semNome > 0 ? [{
            ok: false,
            texto: `${semNome} ${semNome === 1 ? 'contato está' : 'contatos estão'} sem nome, `
                + 'e a variável mapeada para o nome vai sem valor',
        }] : []),
    ];

    /* ── render ─────────────────────────────────────────────── */

    const voltar = (
        <button type="button" className="lk-btn is-ghost" onClick={() => navegar('/campanhas')}>
            <LuChevronLeft size={14} /> Disparador
        </button>
    );

    if (carregando) {
        return (
            <WhatsAppConnectionGuard>
                <div className="page-shell dsp-screen lk-screen">
                    <PageHeader leading={voltar} title="Carregando…" subtitle="Campanha" />
                    <div className="page-body" />
                </div>
            </WhatsAppConnectionGuard>
        );
    }

    /* ─── modo leitura: o resultado ─────────────────────────── */

    if (finalizada && campanha) {
        const enviadas = resumo?.totalDisparado ?? totalDisparos;
        const entregues = resumo?.totalEntregues ?? 0;
        const lidas = resumo?.totalLidas ?? 0;
        const respostas = resumo?.totalComInteracao ?? 0;
        const pct = (n: number) => (enviadas > 0 ? Math.round((n / enviadas) * 100) : 0);
        const totalPaginas = Math.max(1, Math.ceil(totalDisparos / porPagina));

        const linha = (rotulo: string, valor: number) => (
            <div className="dsp-fun">
                <span className="dsp-fun-k">{rotulo}</span>
                <span className="dsp-fun-bar"><i style={{ width: `${pct(valor)}%` }} /></span>
                <span className={`dsp-fun-v ${valor === 0 ? 'is-zero' : ''}`}>{valor}</span>
                <span className="dsp-fun-p">{pct(valor)}%</span>
            </div>
        );

        return (
            <WhatsAppConnectionGuard>
                <div className="page-shell dsp-screen lk-screen">
                    <PageHeader
                        leading={voltar}
                        title={campanha.campanhaNome}
                        subtitle={`${campanha.campanhaStatus} · template ${campanha.campanhaTemplateNome} · ${campanha.campanhaTemplateIdioma}`}
                        actions={podeGerenciar ? (
                            <button type="button" className="lk-btn" onClick={duplicar}>
                                <LuCopy size={14} /> Duplicar
                            </button>
                        ) : undefined}
                    />

                    <div className="page-body dsp-body">
                        <div className="dsp-layout is-leitura">
                            <nav className="dsp-rail">
                                <span className="dsp-rail-t">Ir para</span>
                                <button type="button" className={`dsp-rail-i ${secao === 'resultado' ? 'is-on' : ''}`}
                                    onClick={() => setSecao('resultado')}>
                                    <LuChartNoAxesColumn size={14} /> Resultado
                                </button>
                                <button type="button" className={`dsp-rail-i ${secao === 'disparos' ? 'is-on' : ''}`}
                                    onClick={() => setSecao('disparos')}>
                                    <LuSend size={14} /> Disparos
                                    <span className="dsp-rail-n">{totalDisparos}</span>
                                </button>
                                <button type="button" className={`dsp-rail-i ${secao === 'ficha' ? 'is-on' : ''}`}
                                    onClick={() => setSecao('ficha')}>
                                    <LuIdCard size={14} /> Campanha
                                </button>
                                {/* Histórico (fase 5): criada, editada, destinatários, disparo, excluída. */}
                                <button type="button" className={`dsp-rail-i ${secao === 'historico' ? 'is-on' : ''}`}
                                    onClick={() => setSecao('historico')}>
                                    <LuHistory size={14} /> Histórico
                                </button>
                                {/* Em envio o back recusa a exclusão: os que estão na fila ficariam órfãos. */}
                                {podeGerenciar && !enviando && (
                                    <span className="dsp-rail-ft">
                                        <button type="button" className="dsp-rail-i is-danger"
                                            onClick={() => setConfirmarExclusao(true)}>
                                            <LuTrash2 size={14} /> Excluir campanha
                                        </button>
                                    </span>
                                )}
                            </nav>

                            <div className="dsp-col">
                                {secao === 'resultado' && enviando && (
                                    <div className="dsp-prog" role="status" aria-live="polite">
                                        <div className="dsp-prog-top">
                                            <span className="dsp-prog-t">
                                                <LuLoaderCircle size={15} className="dsp-prog-spin" /> Enviando
                                            </span>
                                            <span className="dsp-prog-pct">{progresso ? `${progresso.percentual}%` : '…'}</span>
                                        </div>
                                        <span className="dsp-fun-bar dsp-prog-bar">
                                            <i style={{ width: `${progresso?.percentual ?? 0}%` }} />
                                        </span>
                                        {progresso && (
                                            <span className="dsp-prog-n">
                                                <strong>{progresso.enviados}</strong> de {progresso.total} {progresso.enviados === 1 ? 'enviada' : 'enviadas'}
                                                {progresso.falhas > 0 && <> · <span className="is-bad">{progresso.falhas} {progresso.falhas === 1 ? 'falha' : 'falhas'}</span></>}
                                                {progresso.pendentes > 0 && <> · {progresso.pendentes} na fila</>}
                                            </span>
                                        )}
                                        <span className="dsp-hint">
                                            O envio continua no servidor mesmo que você saia desta tela. Volte quando
                                            quiser para ver o que já saiu.
                                        </span>
                                    </div>
                                )}

                                {secao === 'resultado' && (
                                    <>
                                        <h2 className="dsp-h">
                                            <LuChartNoAxesColumn size={14} /> Resultado
                                            <span className="dsp-h-em">
                                                {enviadas} {enviadas === 1 ? 'destinatário' : 'destinatários'}
                                            </span>
                                        </h2>
                                        {/* "Disparadas", não "Enviadas": o total conta toda tentativa,
                                            inclusive as que falharam — quem separa uma coisa da outra é
                                            a coluna Status da tabela. */}
                                        {linha('Disparadas', enviadas)}
                                        {linha('Entregues', entregues)}
                                        {linha('Lidas', lidas)}
                                        {linha('Responderam', respostas)}
                                        <span className="dsp-hint">
                                            "Disparadas" conta cada tentativa, inclusive as que falharam;
                                            o desfecho de cada uma está na aba Disparos. "Respondeu" é a
                                            conversa aberta pelo disparo ter mensagem do contato.
                                        </span>
                                    </>
                                )}

                                {secao === 'disparos' && (
                                    <>
                                        <h2 className="dsp-h"><LuSend size={14} /> Disparos</h2>
                                        <div className="lk-table">
                                            <div className="lk-scroll">
                                                <div className="lk-row" style={{ gridTemplateColumns: '150px 96px 1fr 1fr 96px' }}>
                                                    <div className="lk-head">Número</div>
                                                    <div className="lk-head">Status</div>
                                                    <div className="lk-head">Enviado</div>
                                                    <div className="lk-head">Lido</div>
                                                    <div className="lk-head lk-head-acts">Conversa</div>
                                                </div>
                                                <div className="lk-body">
                                                    {disparos.map(d => (
                                                        <div key={d.disparoId} className="lk-row"
                                                            style={{ gridTemplateColumns: '150px 96px 1fr 1fr 96px' }}>
                                                            <span className="lk-cell">
                                                                <span className="dsp-ct">{telefoneBonito(d.disparoNumero)}</span>
                                                            </span>
                                                            <span className="lk-cell">
                                                                <span className={`dsp-d-st is-${rotuloDisparo(d.disparoStatus).tom}`}>
                                                                    {rotuloDisparo(d.disparoStatus).label}
                                                                </span>
                                                            </span>
                                                            <span className="lk-cell lk-muted">{dataHora(d.disparoEnviadoEm)}</span>
                                                            <span className="lk-cell lk-muted">{dataHora(d.disparoLidoEm)}</span>
                                                            <span className="lk-cell lk-acts-cell">
                                                                {d.conversaId
                                                                    ? (
                                                                        <button type="button" className="dsp-conversa"
                                                                            onClick={() => navegar(`/chat?conversa=${d.conversaId}`)}>
                                                                            Abrir <LuExternalLink size={11} />
                                                                        </button>
                                                                    )
                                                                    : <span className="lk-muted">sem conversa</span>}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        {totalDisparos > 0 && (
                                            <div className="lk-foot">
                                                <PerPage value={porPagina} options={[20, 50, 100]} onChange={setPorPagina} />
                                                <span className="lk-range">
                                                    {(paginaDisparos - 1) * porPagina + 1}–
                                                    {Math.min(paginaDisparos * porPagina, totalDisparos)} de {totalDisparos}
                                                </span>
                                                <span className="lk-pages">
                                                    <button type="button" className="lk-page" aria-label="Página anterior"
                                                        disabled={paginaDisparos <= 1}
                                                        onClick={() => setPaginaDisparos(p => p - 1)}>
                                                        <LuChevronLeft size={14} />
                                                    </button>
                                                    <span className="lk-page is-current">{paginaDisparos}</span>
                                                    <span className="lk-muted">de {totalPaginas}</span>
                                                    <button type="button" className="lk-page" aria-label="Próxima página"
                                                        disabled={paginaDisparos >= totalPaginas}
                                                        onClick={() => setPaginaDisparos(p => p + 1)}>
                                                        <LuChevronRight size={14} />
                                                    </button>
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}

                                {secao === 'historico' && campanhaId !== null && (
                                    <>
                                        <h2 className="dsp-h"><LuHistory size={14} /> Histórico</h2>
                                        <div className="dsp-card" style={{ maxWidth: 640 }}>
                                            <SecaoHistorico entidade="campanha" id={campanhaId} variante="pagina" />
                                        </div>
                                    </>
                                )}
                                {secao === 'ficha' && (
                                    <>
                                        <h2 className="dsp-h"><LuIdCard size={14} /> Campanha</h2>
                                        <div className="dsp-card" style={{ maxWidth: 480 }}>
                                            <dl>
                                                <div className="dsp-kv"><dt>Nome</dt><dd>{campanha.campanhaNome}</dd></div>
                                                <div className="dsp-kv">
                                                    <dt>Template</dt>
                                                    <dd>{campanha.campanhaTemplateNome} · {campanha.campanhaTemplateIdioma}</dd>
                                                </div>
                                                <div className="dsp-kv">
                                                    <dt>Resposta</dt>
                                                    <dd>{campanha.redirecionarRespostaParaIA
                                                        ? 'as respostas foram para o Agente de IA'
                                                        : 'as respostas ficaram para atendimento humano'}</dd>
                                                </div>
                                                <div className="dsp-kv">
                                                    <dt>Criada</dt><dd>{dataLonga(campanha.campanhaCreatedat)}</dd>
                                                </div>
                                            </dl>
                                        </div>
                                    </>
                                )}
                            </div>

                            <aside className="dsp-side">
                                <div className="dsp-tel">
                                    <div className="dsp-tel-bar"><LuMegaphone size={14} /> Como foi enviado</div>
                                    <TemplateBubble visual={visualPrevia} resolver roomy />
                                </div>
                                <span className="dsp-nota">
                                    O texto vem do template de hoje. Se ele foi editado depois do disparo,
                                    o que o contato recebeu pode ter sido outro.
                                </span>
                            </aside>
                        </div>
                    </div>

                    <Dialog
                        open={confirmarExclusao}
                        onClose={() => setConfirmarExclusao(false)}
                        title={`Excluir a campanha «${campanha.campanhaNome}»?`}
                        confirmLabel="Excluir"
                        danger
                        onConfirm={excluir}
                    >
                        O resultado e os disparos saem da tela. As conversas que a campanha abriu continuam no Chat.
                    </Dialog>
                </div>
            </WhatsAppConnectionGuard>
        );
    }

    /* ─── modo passos: o rascunho ───────────────────────────── */

    const falta = faltaNoPasso(passo);
    const custo = alvos.length * CUSTO_REFERENCIA;

    const acoes = (
        <div className="lk-actions">
            {passo > 1 && (
                <button type="button" className="lk-btn is-ghost"
                    onClick={() => setPasso(p => (p - 1) as Passo)}>
                    <LuChevronLeft size={14} /> Voltar
                </button>
            )}
            {podeGerenciar && (
                <button type="button" className="lk-btn" onClick={salvarRascunho}
                    disabled={salvando || disparando}>
                    {salvando ? 'Salvando…' : 'Salvar rascunho'}
                </button>
            )}
            {passo < 4 ? (
                <button type="button" className="lk-btn is-primary"
                    disabled={falta !== null}
                    title={falta ?? undefined}
                    onClick={() => setPasso(p => (p + 1) as Passo)}>
                    Avançar <LuArrowRight size={14} />
                </button>
            ) : podeGerenciar && (
                <button type="button" className="lk-btn is-primary"
                    disabled={disparando || alvos.length === 0 || faltaNoPasso(3) !== null}
                    onClick={() => setConfirmarDisparo(true)}>
                    <LuSend size={14} /> {disparando ? 'Disparando…' : 'Confirmar e disparar'}
                </button>
            )}
        </div>
    );

    const subtitulo = campanha
        ? `Rascunho${assinaturaSalva !== null ? ' · salvo' : ''}`
        : 'Rascunho ainda não salvo';

    return (
        <WhatsAppConnectionGuard>
            <div className="page-shell dsp-screen lk-screen">
                <PageHeader
                    leading={voltar}
                    title={nome.trim() || 'Nova campanha'}
                    subtitle={subtitulo}
                    actions={acoes}
                    steps={passosDoHeader}
                    activeStep={String(passo)}
                    onStepChange={p => setPasso(Number(p) as Passo)}
                />

                <div className="page-body dsp-body">
                    <div className="dsp-layout">
                        <div className="dsp-col">
                            {passo === 1 && (
                                <>
                                    <h2 className="dsp-h">Como você quer chamar a campanha?</h2>
                                    <p className="dsp-h-sub">
                                        O nome só aparece aqui e no relatório. O contato nunca vê.
                                    </p>
                                    <div className="dsp-field">
                                        <label className="dsp-lab" htmlFor="dsp-nome">
                                            Nome da campanha<span className="dsp-req">*</span>
                                        </label>
                                        <input id="dsp-nome" className="dsp-inp" value={nome}
                                            placeholder="ex.: Lançamento Jardim Europa"
                                            onChange={e => setNome(e.target.value)} />
                                    </div>
                                    <div className="dsp-field" style={{ marginTop: 18 }}>
                                        <label className="dsp-sw">
                                            <input type="checkbox" checked={paraIA}
                                                onChange={e => setParaIA(e.target.checked)} />
                                            <span className="dsp-sw-track" />
                                            <span>Quem responder cai no Agente de IA</span>
                                        </label>
                                        <span className="dsp-hint">
                                            Desligado, a resposta fica em "Não iniciado" no Chat, para atendimento
                                            humano. Vale para todas as conversas que este disparo abrir.
                                        </span>
                                    </div>
                                </>
                            )}

                            {passo === 2 && (
                                <>
                                    <h2 className="dsp-h">Para quem vai a mensagem?</h2>
                                    <p className="dsp-h-sub">
                                        Escolha da base, digite um telefone ou cole uma lista.
                                        Número repetido entra uma vez só.
                                    </p>

                                    <div className="dsp-pub">
                                        <div>
                                            <div className="dsp-seg" role="tablist">
                                                <button type="button" role="tab" aria-selected={porta === 'base'}
                                                    className={porta === 'base' ? 'is-on' : ''}
                                                    onClick={() => setPorta('base')}>
                                                    <LuContact size={13} /> Da base
                                                </button>
                                                <button type="button" role="tab" aria-selected={porta === 'telefone'}
                                                    className={porta === 'telefone' ? 'is-on' : ''}
                                                    onClick={() => setPorta('telefone')}>
                                                    <LuPhone size={13} /> Por telefone
                                                </button>
                                                <button type="button" role="tab" aria-selected={porta === 'colar'}
                                                    className={porta === 'colar' ? 'is-on' : ''}
                                                    onClick={() => setPorta('colar')}>
                                                    <LuClipboardList size={13} /> Colar lista
                                                </button>
                                            </div>

                                            {porta === 'base' && (
                                                <>
                                                    <div className="lk-filters" style={{ marginBottom: 8 }}>
                                                        <label className="lk-search">
                                                            <input type="search" placeholder="Buscar contato…"
                                                                value={buscaBase}
                                                                onChange={e => setBuscaBase(e.target.value)} />
                                                        </label>
                                                        {organizacoes.length > 0 && (
                                                            <Select size="control" block={false}
                                                                value={organizacao}
                                                                ariaLabel="Organização"
                                                                options={[
                                                                    { value: '', label: 'Todas as organizações' },
                                                                    ...organizacoes.map(o => ({ value: o, label: o })),
                                                                ]}
                                                                onChange={setOrganizacao} />
                                                        )}
                                                    </div>

                                                    <div className="dsp-box">
                                                        <div className="dsp-box-h">
                                                            <b>{selecionados.size} {selecionados.size === 1 ? 'selecionado' : 'selecionados'}</b>
                                                            <span>de {contatosFiltrados.length}</span>
                                                        </div>
                                                        <div className="dsp-box-scroll">
                                                            {contatosFiltrados.length === 0 && (
                                                                <p className="dsp-vazio">Nenhum contato encontrado.</p>
                                                            )}
                                                            {contatosFiltrados.map(c => {
                                                                const tel = sonumeros(c.telefone ?? '');
                                                                const dentro = jaAdicionados.has(tel);
                                                                const marcado = selecionados.has(c.contatoId);
                                                                return (
                                                                    <button type="button" key={c.contatoId}
                                                                        className={`dsp-crow is-base ${marcado ? 'is-on' : ''}`}
                                                                        disabled={dentro}
                                                                        onClick={() => alternarContato(c.contatoId)}>
                                                                        <span className="dsp-cb"><LuCheck size={10} /></span>
                                                                        <span className={`dsp-cn ${!c.nome ? 'is-sem' : ''}`}>
                                                                            {c.nome || 'Sem nome'}
                                                                            {dentro && ' · já na lista'}
                                                                        </span>
                                                                        <span className="dsp-ct">{telefoneBonito(tel)}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <button type="button" className="lk-btn" style={{ marginTop: 10 }}
                                                        disabled={selecionados.size === 0}
                                                        onClick={adicionarSelecionados}>
                                                        Adicionar {selecionados.size > 0 ? `os ${selecionados.size}` : ''} selecionados
                                                    </button>
                                                </>
                                            )}

                                            {porta === 'telefone' && (
                                                <>
                                                    <div className="dsp-field">
                                                        <label className="dsp-lab" htmlFor="dsp-mnome">Nome</label>
                                                        <input id="dsp-mnome" className="dsp-inp" value={manualNome}
                                                            placeholder="opcional"
                                                            onChange={e => setManualNome(e.target.value)} />
                                                    </div>
                                                    <div className="dsp-field">
                                                        <label className="dsp-lab" htmlFor="dsp-mtel">
                                                            Telefone<span className="dsp-req">*</span>
                                                        </label>
                                                        <input id="dsp-mtel" className={`dsp-inp ${manualErro ? 'has-error' : ''}`}
                                                            value={manualTelefone} placeholder="ex.: 5541999999999"
                                                            onChange={e => { setManualTelefone(e.target.value); setManualErro(''); }} />
                                                        {manualErro
                                                            ? <span className="dsp-erro"><LuTriangleAlert size={12} />{manualErro}</span>
                                                            : <span className="dsp-hint">Com DDD. Sem DDI, entra como Brasil (+55).</span>}
                                                    </div>
                                                    <button type="button" className="lk-btn" onClick={adicionarManual}>
                                                        Adicionar contato
                                                    </button>
                                                </>
                                            )}

                                            {porta === 'colar' && (
                                                <>
                                                    <div className="dsp-colar-acoes">
                                                        <label className="dsp-card-ed">
                                                            <LuUpload size={12} /> Importar .csv
                                                            <input type="file" accept=".csv,.txt,text/csv,text/plain"
                                                                hidden onChange={importarArquivo} />
                                                        </label>
                                                        <button type="button" className="dsp-card-ed" onClick={baixarModelo}>
                                                            <LuDownload size={12} /> Baixar modelo
                                                        </button>
                                                    </div>
                                                    <textarea className="dsp-ta" value={colados} rows={6}
                                                        placeholder={'Cole do jeito que tiver — direto do Excel ou Sheets também:\nMaria, (41) 99999-9999\n41988888888; João\n+55 41 97777-7777'}
                                                        onChange={e => setColados(e.target.value)} />
                                                    <span className="dsp-hint">
                                                        {detectados.length} {detectados.length === 1 ? 'número detectado' : 'números detectados'}
                                                        {colagem.repetidos > 0 && ` · ${colagem.repetidos} repetido${colagem.repetidos === 1 ? '' : 's'} na lista`}.
                                                        Nome e telefone em qualquer ordem; sem DDI, entra como Brasil (+55).
                                                    </span>
                                                    {colagem.ignoradas.length > 0 && (
                                                        <div className="dsp-erro dsp-colar-ign">
                                                            <LuTriangleAlert size={12} />
                                                            <span>
                                                                {colagem.ignoradas.length === 1
                                                                    ? '1 linha não vai entrar:'
                                                                    : `${colagem.ignoradas.length} linhas não vão entrar:`}
                                                                <ul>
                                                                    {colagem.ignoradas.slice(0, 5).map((ig, i) => (
                                                                        <li key={i}><code>{ig.linha}</code> — {ig.motivo}</li>
                                                                    ))}
                                                                    {colagem.ignoradas.length > 5 && (
                                                                        <li>e mais {colagem.ignoradas.length - 5}…</li>
                                                                    )}
                                                                </ul>
                                                            </span>
                                                        </div>
                                                    )}
                                                    <button type="button" className="lk-btn" style={{ marginTop: 10 }}
                                                        disabled={detectados.length === 0}
                                                        onClick={adicionarColados}>
                                                        Adicionar lista
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        <div>
                                            <div className="dsp-box">
                                                <div className="dsp-box-h">
                                                    <b>Adicionados · {alvos.length}</b>
                                                    {alvos.length > 0 && (
                                                        <span className="dsp-sp">
                                                            <button type="button" className="dsp-card-ed"
                                                                style={{ color: 'var(--dsp-bad)' }}
                                                                onClick={limparTodos}>Limpar todos</button>
                                                        </span>
                                                    )}
                                                </div>
                                                {alvos.length > 3 && (
                                                    <div className="dsp-box-h">
                                                        <input className="dsp-inp" style={{ height: 26, fontSize: 12 }}
                                                            placeholder="Filtrar por nome ou telefone…"
                                                            value={filtroAdd}
                                                            onChange={e => setFiltroAdd(e.target.value)} />
                                                    </div>
                                                )}
                                                <div className="dsp-box-scroll">
                                                    {alvos.length === 0 && (
                                                        <p className="dsp-vazio">
                                                            Ninguém ainda. Escolha ao lado e adicione.
                                                        </p>
                                                    )}
                                                    {alvosFiltrados.map(a => (
                                                        <div key={a.telefone} className="dsp-crow is-add">
                                                            <span className={`dsp-cn ${!a.nome ? 'is-sem' : ''}`}>
                                                                {a.nome || 'Sem nome'}
                                                            </span>
                                                            <span className="dsp-ct">{telefoneBonito(a.telefone)}</span>
                                                            <span className="dsp-co">{ORIGEM_ROTULO[a.origem]}</span>
                                                            <button type="button" className="dsp-rm"
                                                                aria-label={`Remover ${a.nome ?? a.telefone}`}
                                                                onClick={() => removerAlvo(a)}>
                                                                <LuX size={13} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <span className="dsp-hint">
                                                Quem já respondeu a outra campanha continua entrando: não há supressão automática.
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {passo === 3 && (
                                <>
                                    <h2 className="dsp-h">O que a mensagem vai dizer?</h2>
                                    <p className="dsp-h-sub">
                                        Só templates aprovados na Meta entram na lista. Para mudar o texto,
                                        edite o template em Templates: aqui só se preenche o que ele pede.
                                    </p>

                                    <div className="dsp-field">
                                        <label className="dsp-lab">Template<span className="dsp-req">*</span></label>
                                        <Select
                                            value={templateNome}
                                            onChange={escolherTemplate}
                                            placeholder={templates.length ? 'Escolha um template' : 'Nenhum template aprovado'}
                                            emptyText="Nenhum template aprovado na Meta."
                                            ariaLabel="Template"
                                            options={templates.map(t => {
                                                const v = lerComponentes(t.componentesJson);
                                                const qtd = [...v.textoBody.matchAll(/\{\{(\d+)\}\}/g)].length;
                                                const partes = [t.idioma];
                                                if (v.formatoHeader && v.formatoHeader !== 'TEXT') {
                                                    partes.push(v.formatoHeader.toLowerCase());
                                                }
                                                if (qtd > 0) partes.push(`${qtd} ${qtd === 1 ? 'variável' : 'variáveis'}`);
                                                if (v.botoes.length > 0) {
                                                    partes.push(`${v.botoes.length} ${v.botoes.length === 1 ? 'botão' : 'botões'}`);
                                                }
                                                return { value: t.nome, label: t.nome, meta: partes.join(' · ') };
                                            })}
                                        />
                                    </div>

                                    {template && totalVars > 0 && (
                                        <div className="dsp-vars">
                                            <p className="dsp-vars-t">
                                                {totalVars === 1 ? 'A variável do corpo' : `As ${totalVars} variáveis do corpo`}
                                            </p>
                                            {mapeamentos.map((m, i) => (
                                                <div className="dsp-vars-r" key={i}>
                                                    <span className="dsp-vars-k">{`{{${i + 1}}}`}</span>
                                                    <span className="dsp-vars-sel">
                                                        <Select
                                                            size="sm"
                                                            value={m.fonte}
                                                            options={FONTE_OPCOES}
                                                            ariaLabel={`Origem da variável ${i + 1}`}
                                                            onChange={v => setMapeamentos(antes => antes.map((x, j) =>
                                                                j === i ? { fonte: v as FonteVar, valorFixo: '' } : x))}
                                                        />
                                                    </span>
                                                    {m.fonte === 'fixo' && (
                                                        <input className="dsp-inp" style={{ height: 30, flex: 1 }}
                                                            placeholder="Valor"
                                                            value={m.valorFixo}
                                                            onChange={e => setMapeamentos(antes => antes.map((x, j) =>
                                                                j === i ? { ...x, valorFixo: e.target.value } : x))} />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {template && indiceCupom !== -1 && (
                                        <div className="dsp-field dsp-sec-gap">
                                            <label className="dsp-lab" htmlFor="dsp-cupom">
                                                Código do cupom<span className="dsp-req">*</span>
                                            </label>
                                            <input id="dsp-cupom" className="dsp-inp" value={cupom}
                                                placeholder="ex.: BLACK30" onChange={e => setCupom(e.target.value)} />
                                            <span className="dsp-hint">O template tem um botão de copiar código.</span>
                                        </div>
                                    )}

                                    {template && indiceUrlVar !== -1 && (
                                        <div className="dsp-field dsp-sec-gap">
                                            <label className="dsp-lab" htmlFor="dsp-url">
                                                Variável do link<span className="dsp-req">*</span>
                                            </label>
                                            <input id="dsp-url" className="dsp-inp" value={urlVar}
                                                placeholder="ex.: cupom-natal" onChange={e => setUrlVar(e.target.value)} />
                                            <span className="dsp-hint">Completa o final da URL do botão.</span>
                                        </div>
                                    )}

                                    {template && pedeLocalizacao && (
                                        <div className="dsp-sec-gap">
                                            <h3 className="dsp-h" style={{ fontSize: 13.5 }}>
                                                <LuMegaphone size={14} /> Cabeçalho de localização
                                                <span className="dsp-req">*</span>
                                            </h3>
                                            <p className="dsp-h-sub">
                                                Este template abre com um mapa. A Meta exige nome, endereço e coordenadas.
                                            </p>
                                            <div className="dsp-field">
                                                <label className="dsp-lab" htmlFor="dsp-lnome">Nome do local</label>
                                                <input id="dsp-lnome" className="dsp-inp" value={local.nome}
                                                    onChange={e => setLocal(l => ({ ...l, nome: e.target.value }))} />
                                            </div>
                                            <div className="dsp-field">
                                                <label className="dsp-lab" htmlFor="dsp-lend">Endereço</label>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <input id="dsp-lend" className="dsp-inp" value={local.endereco}
                                                        onChange={e => setLocal(l => ({ ...l, endereco: e.target.value }))} />
                                                    <button type="button" className="lk-btn" style={{ flex: 'none' }}
                                                        disabled={buscandoCoord || !local.endereco.trim()}
                                                        onClick={buscarCoordenadas}>
                                                        {buscandoCoord ? 'Buscando…' : 'Buscar coordenadas'}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="dsp-row2">
                                                <div className="dsp-field">
                                                    <label className="dsp-lab" htmlFor="dsp-lat">Latitude</label>
                                                    <input id="dsp-lat" className="dsp-inp" value={local.lat}
                                                        onChange={e => setLocal(l => ({ ...l, lat: e.target.value }))} />
                                                </div>
                                                <div className="dsp-field">
                                                    <label className="dsp-lab" htmlFor="dsp-lng">Longitude</label>
                                                    <input id="dsp-lng" className="dsp-inp" value={local.lng}
                                                        onChange={e => setLocal(l => ({ ...l, lng: e.target.value }))} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {template && totalVars === 0 && indiceCupom === -1
                                        && indiceUrlVar === -1 && !pedeLocalizacao && (
                                            <p className="dsp-h-sub">
                                                Este template não pede nada além do texto: ele sai igual para todo mundo.
                                            </p>
                                        )}
                                </>
                            )}

                            {passo === 4 && (
                                <>
                                    <h2 className="dsp-h">Tudo certo para disparar?</h2>
                                    <p className="dsp-h-sub">
                                        Cada bloco leva de volta ao passo. O disparo não tem pausa nem volta:
                                        a Meta entrega em segundos.
                                    </p>

                                    <div className="dsp-rev">
                                        <div className="dsp-card">
                                            <div className="dsp-card-t">
                                                Campanha
                                                <button type="button" className="dsp-card-ed"
                                                    onClick={() => setPasso(1)}>Editar</button>
                                            </div>
                                            <dl>
                                                <div className="dsp-kv"><dt>Nome</dt><dd>{nome}</dd></div>
                                                <div className="dsp-kv">
                                                    <dt>Resposta</dt>
                                                    <dd>{paraIA ? 'vai para o Agente de IA' : 'atendimento humano'}</dd>
                                                </div>
                                            </dl>
                                        </div>

                                        <div className="dsp-card">
                                            <div className="dsp-card-t">
                                                Público
                                                <button type="button" className="dsp-card-ed"
                                                    onClick={() => setPasso(2)}>Editar</button>
                                            </div>
                                            <dl>
                                                <div className="dsp-kv">
                                                    <dt>Destinatários</dt>
                                                    <dd className="is-big">{alvos.length}</dd>
                                                </div>
                                                <div className="dsp-kv">
                                                    <dt>Origem</dt>
                                                    <dd>{(['base', 'colado', 'telefone'] as const)
                                                        .map(o => ({ o, n: alvos.filter(a => a.origem === o).length }))
                                                        .filter(x => x.n > 0)
                                                        .map(x => `${x.n} ${ORIGEM_ROTULO[x.o]}`)
                                                        .join(' · ') || '—'}</dd>
                                                </div>
                                            </dl>
                                        </div>

                                        <div className="dsp-card">
                                            <div className="dsp-card-t">
                                                Conteúdo
                                                <button type="button" className="dsp-card-ed"
                                                    onClick={() => setPasso(3)}>Editar</button>
                                            </div>
                                            <dl>
                                                <div className="dsp-kv">
                                                    <dt>Template</dt>
                                                    <dd>{templateNome} · {idiomaDoTemplate}</dd>
                                                </div>
                                                {mapeamentos.map((m, i) => (
                                                    <div className="dsp-kv" key={i}>
                                                        <dt>{`{{${i + 1}}}`}</dt>
                                                        <dd>{m.fonte === 'fixo'
                                                            ? m.valorFixo
                                                            : FONTE_OPCOES.find(f => f.value === m.fonte)?.label}</dd>
                                                    </div>
                                                ))}
                                                {pedeLocalizacao && (
                                                    <div className="dsp-kv">
                                                        <dt>Localização</dt>
                                                        <dd>{local.nome} · {local.lat}, {local.lng}</dd>
                                                    </div>
                                                )}
                                            </dl>
                                        </div>

                                        <div className="dsp-card">
                                            <div className="dsp-card-t">Custo</div>
                                            <dl>
                                                <div className="dsp-kv">
                                                    <dt>Estimado</dt>
                                                    <dd className="is-big">≈ {moeda(custo)}</dd>
                                                </div>
                                                <div className="dsp-kv is-nota">
                                                    Referência de {moeda(CUSTO_REFERENCIA)} por envio.
                                                    A Meta cobra por categoria e país.
                                                </div>
                                            </dl>
                                        </div>
                                    </div>

                                    <div className="dsp-card" style={{ maxWidth: 720 }}>
                                        <div className="dsp-card-t">Antes de disparar</div>
                                        {checagens.map((c, i) => (
                                            <p key={i} className={`dsp-ck ${c.ok ? '' : 'is-aviso'}`}>
                                                {c.ok ? <LuCheck size={13} /> : <LuTriangleAlert size={13} />}
                                                <span>{c.texto}</span>
                                            </p>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <aside className="dsp-side">
                            <div className="dsp-tel">
                                <div className="dsp-tel-bar">
                                    <LuMegaphone size={14} />
                                    {alvos[0]?.nome
                                        ? `Como ${alvos[0].nome.split(' ')[0]} vai receber`
                                        : 'Pré-visualização'}
                                </div>
                                <TemplateBubble
                                    visual={visualPrevia}
                                    resolver
                                    roomy
                                    vazio="A mensagem aparece aqui quando você escolher o template, no passo 3."
                                />
                            </div>

                            <div className="dsp-card">
                                <div className="dsp-card-t">Resumo</div>
                                <dl>
                                    <div className="dsp-kv">
                                        <dt>Campanha</dt>
                                        <dd className={nome.trim() ? '' : 'is-mu'}>{nome.trim() || 'passo 1'}</dd>
                                    </div>
                                    <div className="dsp-kv">
                                        <dt>Público</dt>
                                        {alvos.length > 0
                                            ? <dd className="is-big">{alvos.length}</dd>
                                            : <dd className="is-mu">passo 2</dd>}
                                    </div>
                                    <div className="dsp-kv">
                                        <dt>Template</dt>
                                        <dd className={templateNome ? '' : 'is-mu'}>{templateNome || 'passo 3'}</dd>
                                    </div>
                                    <div className="dsp-kv">
                                        <dt>Resposta</dt>
                                        <dd>{paraIA ? 'vai para o Agente de IA' : 'atendimento humano'}</dd>
                                    </div>
                                </dl>
                            </div>

                            {campanha && podeGerenciar && (
                                <button type="button" className="lk-btn is-ghost" style={{ marginTop: 12 }}
                                    onClick={() => setConfirmarExclusao(true)}>
                                    <LuTrash2 size={14} /> Excluir campanha
                                </button>
                            )}
                        </aside>
                    </div>
                </div>

                <Dialog
                    open={confirmarDisparo}
                    onClose={() => setConfirmarDisparo(false)}
                    title={`Disparar agora para ${alvos.length} ${alvos.length === 1 ? 'contato' : 'contatos'}?`}
                    confirmLabel="Disparar"
                    cancelLabel="Ainda não"
                    busy={disparando}
                    onConfirm={dispararAgora}
                >
                    O envio segue no servidor, mesmo que você feche a tela, e não há como interromper depois de confirmar.
                    {usaNome && semNome > 0 && ` ${semNome} ${semNome === 1 ? 'contato está' : 'contatos estão'} sem nome.`}
                </Dialog>

                <Dialog
                    open={disparoIniciado !== null}
                    onClose={() => navegar('/campanhas')}
                    title="Disparo iniciado"
                    confirmLabel="Acompanhar envio"
                    cancelLabel="Sair"
                    onConfirm={acompanharEnvio}
                >
                    {disparoIniciado && (
                        <>
                            {disparoIniciado.totalNaFila} {disparoIniciado.totalNaFila === 1 ? 'mensagem entrou' : 'mensagens entraram'} na
                            fila de envio. Você pode acompanhar daqui ou sair: o envio continua, e a campanha mostra o
                            andamento sempre que for aberta.
                            {disparoIniciado.totalIgnorados > 0 && ` ${disparoIniciado.totalIgnorados} ${disparoIniciado.totalIgnorados === 1 ? 'contato ficou' : 'contatos ficaram'} de fora por não ter número.`}
                        </>
                    )}
                </Dialog>

                <Dialog
                    open={confirmarExclusao}
                    onClose={() => setConfirmarExclusao(false)}
                    title={`Excluir a campanha «${nome.trim() || 'sem nome'}»?`}
                    confirmLabel="Excluir"
                    danger
                    onConfirm={excluir}
                >
                    O rascunho e a lista de contatos saem da tela.
                </Dialog>
            </div>
        </WhatsAppConnectionGuard>
    );
};

export default CampanhaPage;
